import styled from '@emotion/styled';
import * as React from 'react';
import Icons from '../images/Icons';
import { LightLarge, TitleMedium, TitleSmall } from '../text/Typography';
import { HeaderChildWrap, HeaderTextWrap, HeadingDescription } from './CommonHeaderStyles';

export interface ISubSectionRowProps {
    title: string;
    prefixElement?: React.ReactNode; // for example, a tag
    children?: React.ReactNode;
    description?: string; // does not show in small variant
    variant?: 'default' | 'small';
    $sticky?: boolean;
    complete?: boolean;
    number?: number;
    tabIndex?: number; // for scroll to
    scrollId?: string; // for scroll to
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'; // default is h2
    id?: string; // for scroll to
}

export default function SubSectionRow({
    title = '',
    prefixElement,
    children,
    description,
    complete,
    number,
    tabIndex,
    scrollId,
    as = 'h2',
    variant = 'default',
    ...restProps
}: ISubSectionRowProps) {
    if (complete) variant = 'small';
    const BodyComponent = variant == 'small' || !!complete ? LightLarge : TitleMedium;
    return (
        <RowWrapper {...restProps} $variant={variant}>
            {(number || complete) && (
                <Step>
                    {complete ? <Icons name="check" /> : <StyledTitleSmall as="span">{number}</StyledTitleSmall>}
                </Step>
            )}
            <HeaderTextWrap>
                {prefixElement}
                <BodyComponent as={as} tabIndex={tabIndex} id={scrollId}>
                    {title}
                </BodyComponent>
                {description && variant != 'small' && <HeadingDescription>{description}</HeadingDescription>}
            </HeaderTextWrap>
            <HeaderChildWrap>{children}</HeaderChildWrap>
        </RowWrapper>
    );
}

const RowWrapper = styled.div<{ $variant: 'default' | 'small'; $sticky?: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 1rem;
    position: ${(props) => (props.$sticky ? 'sticky' : 'static')};
    top: 1.5rem;
    padding-top: ${(props) => (props.$variant == 'small' ? '0.25rem' : '0.5rem')};
    padding-bottom: ${(props) => (props.$variant == 'small' ? '0.25rem' : '0.5rem')};
    margin: ${(props) => (props.$variant == 'small' ? '1rem 0 0 0' : '2rem 0 0 0')};
    border-bottom-width: ${(props) => (props.$variant == 'small' ? 1 : 2)}px;
    border-style: solid;
    border-color: ${(props) => (props.$variant == 'small' ? props.theme.color.border : props.theme.color.textBody)};
    background-color: ${(props) => props.theme.color.background};
    z-index: 10;
`;

const Step = styled.div`
    background-color: ${(props) => props.theme.color.text};
    color: ${(props) => props.theme.color.background};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    width: 2rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
`;

const StyledTitleSmall = styled(TitleSmall)`
    color: ${(props) => props.theme.color.background};
`;
